import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoogleOAuthButton from "../components/auth/GoogleOAuthButton";
import "../styles/pages/Login.css";
import API_BASE, { setCsrfToken, withApiCredentials } from "../api";
import {
  getDeletedAccountAuthMessage,
  isDeletedAccountAuthError,
} from "../auth/deletedAccountMessage";
export default function Login({ setUser }) {
  const { t } = useTranslation();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      alert(t("login_error_missing_fields"));
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("username", username.trim());
      formData.append("password", password);

      const res = await fetch(`${API_BASE}/auth/login`, withApiCredentials({
        method: "POST",
        body: formData, // ✅ IMPORTANT: NO headers, NO JSON
      }));

      if (!res.ok) {
        let errorDetail = "";
        try {
          const error = await res.json();
          errorDetail = error?.detail || "";
        } catch {
          errorDetail = "";
        }

        if (errorDetail === "email_verification_required") {
          setIsLoading(false);
          alert(
            t("login_error_verification_required", {
              defaultValue: "Please verify your email before logging in.",
            })
          );
          return;
        }

        if (isDeletedAccountAuthError(errorDetail)) {
          setIsLoading(false);
          alert(getDeletedAccountAuthMessage(t));
          return;
        }

        if (
          errorDetail === "account_not_ready" ||
          errorDetail === "linked_profile_missing"
        ) {
          setIsLoading(false);
          alert(
            t("login_error_account_not_ready", {
              defaultValue:
                "Your account is not ready for login yet. Please try again later or contact support.",
            })
          );
          return;
        }

        if (errorDetail === "supabase_login_unavailable") {
          setIsLoading(false);
          alert(t("login_error_server"));
          return;
        }

        setIsLoading(false);
        alert(t("login_error_invalid"));
        return;
      }

      const data = await res.json();
      if (typeof data?.csrfToken === "string" && data.csrfToken.trim()) {
        setCsrfToken(data.csrfToken);
      }
      setIsLoading(false);
      setUser(data.user);
    } catch (err) {
      setIsLoading(false);
      console.error("Login error:", err);
      alert(t("login_error_server"));
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-branding" aria-label={t("login_branding_aria")}>
          <img
            src="/banner/logo.png"
            alt={t("login_logo_alt")}
            className="login-branding-logo"
          />
          <p className="login-branding-eyebrow">{t("login_brand_eyebrow")}</p>
          <h1 className="login-branding-title">{t("login_brand_title")}</h1>
          <p className="login-branding-subtitle">
            {t("login_brand_subtitle")}
          </p>
        </div>

        <h2 className="login-title">{t("login_title")}</h2>

        <div className="login-oauth-block">
          <GoogleOAuthButton />
          <div className="login-oauth-divider">
            <span>{t("oauth_or_email", { defaultValue: "or" })}</span>
          </div>
        </div>

        <form onSubmit={handleLogin}>
          <label htmlFor="login-username" className="sr-only">
            {t("username_or_email")}
          </label>
          <input
            id="login-username"
            name="username"
            autoComplete="username"
            type="text"
            placeholder={t("username_or_email")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="login-input"
          />

          <div className="password-wrapper">
            <label htmlFor="login-password" className="sr-only">
              {t("password")}
            </label>
            <input
              id="login-password"
              name="password"
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              placeholder={t("password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-input password-input"
            />

            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="toggle-password-btn"
            >
              {showPassword ? t("hide") : t("show")}
            </button>
          </div>

          <button type="submit" className="login-btn" disabled={isLoading}>
            {isLoading
              ? t("login_loading", { defaultValue: "Signing in…" })
              : t("login_button")}
          </button>
        </form>

        <p className="login-forgot-password">
          <Link to="/forgot-password" className="login-forgot-link">
            {t("forgot_password_link", { defaultValue: "Forgot password?" })}
          </Link>
        </p>

        <p className="login-footer login-register-callout">
          {t("no_account")}{" "}
          <Link to="/register" className="login-register-link">
            {t("register_here")}
          </Link>
        </p>

        <div className="login-legal-links" aria-label={t("landing_footer_legal_aria")}>
          <Link to="/legal/terms" className="login-legal-link">
            {t("legal_document_terms_title")}
          </Link>
          <span className="login-legal-separator">|</span>
          <Link to="/legal/privacy" className="login-legal-link">
            {t("legal_document_privacy_title")}
          </Link>
          <span className="login-legal-separator">|</span>
          <Link to="/legal/attributions" className="login-legal-link">
            {t("legal_document_attributions_title")}
          </Link>
        </div>
      </div>
    </div>
  );
}
